<template>
  <div class="news-page">
    <PageHeader
        :title="$t('newsPage.header')"
        :subtitle="$t('newsPage.subtitle')"
        :breadcrumb-items="[{ text: $t('newsPage.breadcrumb.home'), path: '/' }]"
        :current-text="$t('newsPage.breadcrumb.news')"
    />

    <!-- 降级数据提示 -->
    <el-alert
        v-if="isFallback"
        :title="errorMessage"
        type="info"
        :closable="false"
        center
        show-icon
        style="margin-bottom: 20px;"
    />

    <div class="page-container">
      <!-- 分类筛选 -->
      <div class="category-filter">
        <el-radio-group v-model="selectedCategory" @change="handleCategoryChange">
          <el-radio-button :label="null">{{ $t('newsPage.allCategories') }}</el-radio-button>
          <el-radio-button
              v-for="category in categories"
              :key="category.id"
              :label="category.id"
          >
            {{ category.name }}
          </el-radio-button>
        </el-radio-group>
      </div>

      <!-- 新闻列表 -->
      <div class="news-list" v-loading="loading">
        <div class="row">
          <div class="col" v-for="item in filteredData" :key="item._id">
            <ArticleCard
                :id="item._id"
                :title="item.title"
                :summary="getSummary(item.content)"
                :cover="item.cover"
                :date="item.editTime"
                :category="getCategoryName(item.category)"
                type="news"
                @view="viewDetail"
            />
          </div>
        </div>

        <!-- 空状态 -->
        <el-empty
            v-if="!loading && filteredData.length === 0"
            :description="$t('newsPage.noNews')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { getNewsList, getNewsCategories } from '@/api/news';
import { usePagination } from '@/utils/useApi';
import { getSummary } from '@/utils/helper';
import PageHeader from '@/components/PageHeader.vue';
import ArticleCard from '@/components/ArticleCard.vue';
import Pagination from '@/components/Pagination.vue';
import { useI18n } from 'vue-i18n';

export default {
  name: 'NewsView',
  components: {
    PageHeader,
    ArticleCard,
    Pagination
  },
  setup() {
    const { t, locale } = useI18n();
    const router = useRouter();

    const selectedCategory = ref(null);
    const rawCategories = ref([]);
    const pageSize = ref(10);
    const isFallback = ref(false);
    const errorMessage = ref('');

    // ✅ fallback 分类：只保留 id，在 computed 中翻译
    const fallbackCategoryIds = [1, 2, 3];

    // ✅ fallback 新闻内容响应式 computed
    const fallbackNewsItems = computed(() => [
      {
        _id: 'news1',
        title: t('newsPage.fallback.news1.title'),
        content: t('newsPage.fallback.news1.content'),
        category: 1,
        cover: '',
        editTime: new Date().toISOString(),
        languages: ['zh']
      },
      {
        _id: 'news2',
        title: t('newsPage.fallback.news2.title'),
        content: t('newsPage.fallback.news2.content'),
        category: 2,
        cover: '',
        editTime: new Date(Date.now() - 3 * 86400000).toISOString(),
        languages: ['en']
      }
    ]);

    // ✅ 使用 fallbackNewsItems（computed）本身，不加 `.value`
    const {
      data,
      error,
      loading,
      currentPage,
      total,
      loadData,
      changePage,
      changePageSize,
      refresh,
      isFallback: apiIsFallback
    } = usePagination(getNewsList, {
      pageSize: pageSize.value,
      fallbackData: fallbackNewsItems
    });

    // ✅ 动态翻译分类名
    const getCategoryNameById = (id) => {
      switch (id) {
        case 1:
          return t('newsPage.categories.company');
        case 2:
          return t('newsPage.categories.industry');
        case 3:
          return t('newsPage.categories.tech');
        default:
          return t('newsPage.uncategorized');
      }
    };

    // ✅ 分类使用 computed，确保 name 会随语言切换更新
    const categories = computed(() => {
      return rawCategories.value.map(cat => ({
        id: cat.id,
        name: getCategoryNameById(cat.id)
      }));
    });

    const getCategoryName = (id) => getCategoryNameById(id);

    const fetchCategories = async () => {
      try {
        const res = await getNewsCategories();
        if (res.data?.ActionType === 'ok' && res.data.data?.length) {
          rawCategories.value = res.data.data;
        } else {
          isFallback.value = true;
          errorMessage.value = t('newsPage.fallbackMessage');
          rawCategories.value = fallbackCategoryIds.map(id => ({id}));
        }
      } catch (e) {
        console.error(e);
        isFallback.value = true;
        errorMessage.value = t('newsPage.fallbackMessageAlt');
        rawCategories.value = fallbackCategoryIds.map(id => ({id}));
      }
    };

    const viewDetail = (id) => {
      router.push(`/news/${id}`);
    };

    const handleCategoryChange = () => {
      loadData({
        page: 1,
        category: selectedCategory.value
      });
    };

    watch(selectedCategory, handleCategoryChange);

    watch(apiIsFallback, (newVal) => {
      isFallback.value = newVal;
      if (newVal) {
        errorMessage.value = t('newsPage.fallbackMessage');
      }
    });

    onMounted(() => {
      fetchCategories();
      loadData();
    });

    // ✅ 根据当前语言过滤新闻
    const filteredData = computed(() => {
      const currentLang = locale.value;
      return data.value.filter(item => item.languages.includes(currentLang));
    });

    return {
      data,
      error,
      loading,
      currentPage,
      total,
      selectedCategory,
      categories,
      pageSize,
      getCategoryName,
      getSummary,
      viewDetail,
      changePage,
      changePageSize,
      handleCategoryChange,
      isFallback,
      errorMessage,
      filteredData
    };
  }
};
</script>

<style lang="scss" scoped>
.news-page {
  padding-top: 100px;

  .category-filter {
    margin-bottom: 30px;
  }

  .news-list {
    margin-bottom: 30px;
    min-height: 300px;

    .row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 30px;

      @media (max-width: 1200px) {
        grid-template-columns: repeat(2, 1fr);
      }

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
      }

      .col {
        margin-bottom: 30px;
      }
    }
  }
}
</style>
